import { useSessionStore } from "@auth/sessionStore";
import { CompanionHeader } from "@components/CompanionHeader";
import { CompanionPanel } from "@components/CompanionPanel";
import { useUser } from "@context/UserContext";
import { useTaskActions } from "@notifications/useTaskActions";
import {
  Button,
  PanelError,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useSearch } from "@tanstack/react-router";
import { TaskIndexCard } from "@tasks/TaskIndexCard";
import { type AssignedTo, taskKindLabel } from "@tasks/tasksClient";
import { useOpenTasks } from "@tasks/tasksQueries";
import { useSignCeremony } from "@tasks/useSignCeremony";
import { isLeadership } from "@users/userClient";
import { useMemo, useState } from "react";

type TasksSearch = { kind?: string; urgent?: true };

const title = ({ kind, urgent }: TasksSearch): string => {
  if (urgent && kind) return `${taskKindLabel(kind)} (urgent)`;
  if (urgent) return "Urgent tasks";
  if (kind) return taskKindLabel(kind);
  return "Open tasks";
};

/**
 * The whole clinic's open tasks, as a stack of index cards on a page (WEB-3328).
 * A mono eyebrow says whose stack it is, and for leadership doubles as the switch
 * between the two.
 */
export const TasksRoute = () => {
  const search = useSearch({ from: "/tasks" });
  const { currentUser } = useUser();
  const leadership = isLeadership(currentUser);
  // Everyone (leadership included) defaults to their own user/role tasks;
  // leadership alone can flip to the whole-clinic view (BE-2089). The toggle
  // lives here, outside the suspense panel, so it stays put while the list
  // refetches on switch.
  const [showAll, setShowAll] = useState(false);
  const assignedTo: AssignedTo = leadership && showAll ? "all" : "me";

  return (
    <div className="flex h-screen w-screen flex-col bg-paper text-ink-body">
      <CompanionHeader back={{ title: title(search), testId: "tasks-back" }} />

      <main
        aria-label="Tasks"
        className="flex min-w-0 flex-1 flex-col overflow-y-auto px-6 py-4"
      >
        {/* Capped and centred: a card the width of a monitor stops reading as a
            card, and a written line that long stops being scannable. */}
        <div className="mx-auto flex w-full max-w-3xl flex-col">
          <div className="mb-3 flex items-center gap-3">
            <span className="min-w-0 truncate text-annotation-caps text-ink">
              {showAll ? "All clinic tasks" : "My tasks"}
            </span>
            {leadership && (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                data-testid="tasks-scope-toggle"
                onClick={() => setShowAll((v) => !v)}
              >
                {showAll ? "Show only my tasks" : "Show all clinic tasks"}
              </Button>
            )}
          </div>
          <CompanionPanel
            skeleton={<TasksSkeleton />}
            fallback={(retry) => <PanelError label="tasks" onRetry={retry} />}
          >
            <ClinicTasksList
              kind={search.kind}
              urgent={search.urgent}
              assignedTo={assignedTo}
            />
          </CompanionPanel>
        </div>
      </main>
    </div>
  );
};

const ClinicTasksList = ({
  kind,
  urgent,
  assignedTo,
}: TasksSearch & { assignedTo: AssignedTo }) => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } = useOpenTasks(
    sessionKey,
    kind,
    urgent,
    assignedTo,
  );
  const { handleAction, isAnswered } = useTaskActions();

  // status/kind/urgent all filter server-side, and the server orders the list
  // urgent-first then by due date (BE-2133) — so we render pages in the order
  // received (no client re-sort, which would only reorder loaded pages);
  // just-resolved tasks drop out locally.
  const visible = useMemo(
    () =>
      data.pages
        .flatMap((page) => page.tasks)
        .filter((task) => !isAnswered(task.id)),
    [data.pages, isAnswered],
  );

  // A signed card is held here for its stamp before it leaves the list: the
  // signature closing the task is the same event that drops it from the data.
  const { rows, onSigning, onSignSettled } = useSignCeremony(visible);

  return (
    // Cards are paper objects that lift off the page and ring outside their own
    // box, so the stack is spaced rather than packed.
    <div className="flex flex-col gap-4">
      {rows.length === 0 && (
        <p className="text-sm text-annotation">
          Nothing matches those filters.
        </p>
      )}
      {rows.map(({ task, stamped }, index) => (
        <TaskIndexCard
          key={task.id}
          task={task}
          severity={task.severity}
          handleAction={handleAction}
          stamped={stamped}
          onSigning={() => onSigning(task, index)}
          onSignSettled={(signed) => onSignSettled(task, signed)}
        />
      ))}
      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          data-testid="tasks-load-more"
          disabled={isFetchingNextPage}
          onClick={() => fetchNextPage()}
        >
          {isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      )}
    </div>
  );
};

const TasksSkeleton = () => (
  // Card-height blanks: a ruled face and its strip come to ~7.5rem.
  <SkeletonStack count={4} className="gap-4" itemClassName="h-30" />
);
